import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _taggedTemplateLiteral from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/taggedTemplateLiteral.js";

var _templateObject;

import isPropValid from '@emotion/is-prop-valid';
import { BadgesProgressModal, ClientSideRender, MediaHtml } from '@infl-ts/ui-advocate-components';
import { useToggle } from '@infl-ts/util-hooks';
import { createChallengePlayerLink } from '@infl-ts/util-use-challenge-player';
import { Box, css, styled, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import * as A from 'fp-ts/Array';
import { constUndefined, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { getLocale } from '@infl-ts/util-date';
import { useLanguage } from '@infl-ts/util-i18n-provider';
import { Challenge, PublicResponse, PublicResponseComment } from './codecs';
import ItemImage from './item-image';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var StyledNotificationItem = styled(Box, {
  shouldForwardProp: isPropValid
})(function (_ref) {
  var _ref$hoverEffect = _ref.hoverEffect,
      hoverEffect = _ref$hoverEffect === void 0 ? true : _ref$hoverEffect,
      theme = _ref.theme;
  return hoverEffect && css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n      &:hover {\n        background-color: ", ";\n      }\n    "])), theme.palette.action.hover);
});

var NoClick = function NoClick(_ref2) {
  var children = _ref2.children;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};

var AvailableBadgesModal = function AvailableBadgesModal(_ref3) {
  var children = _ref3.children;

  var _useToggle = useToggle(),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      isOpen = _useToggle2[0],
      _useToggle2$ = _useToggle2[1],
      on = _useToggle2$.on,
      off = _useToggle2$.off;

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(BadgesProgressModal, {
      isOpen: isOpen,
      onClose: off,
      "data-element": "BadgesProgressModal",
      "data-component": "AvailableBadgesModal",
      "data-source-file": "notification-item.tsx"
    }), /*#__PURE__*/_jsx(Box, {
      onClick: on,
      "data-element": "Box",
      "data-source-file": "notification-item.tsx",
      children: children
    })]
  });
};

var ClickHandlerComponent = function ClickHandlerComponent(_ref4) {
  var eventType = _ref4.eventType,
      children = _ref4.children;

  switch (eventType) {
    case 'advanced_level':
    case 'earned_badge':
      return /*#__PURE__*/_jsx(AvailableBadgesModal, {
        children: children
      });

    default:
      return /*#__PURE__*/_jsx(NoClick, {
        children: children
      });
  }
};

var OpenChallengePlayer = function OpenChallengePlayer(_ref5) {
  var challengeLink = _ref5.challengeLink,
      children = _ref5.children;
  var history = useHistory();
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      cursor: 'pointer',
      '&:hover a': {
        textDecoration: 'underline'
      },
      '& a': {
        pointerEvents: 'none'
      }
    },
    onClick: function onClick(e) {
      e.preventDefault();
      history.push(challengeLink);
    },
    "data-element": "Box",
    "data-component": "OpenChallengePlayer",
    "data-source-file": "notification-item.tsx",
    children: children
  });
};

var getFirstCompletedActivityId = function getFirstCompletedActivityId(activities) {
  return pipe(activities, O.fromNullable, O.chain(A.findFirst(function (activity) {
    return activity.state === 'closed';
  })), O.match(constUndefined, function (activity) {
    return activity.id;
  }));
};

var ClickHandler = function ClickHandler(_ref6) {
  var notification = _ref6.notification,
      children = _ref6.children;

  var _useLocation = useLocation(),
      pathname = _useLocation.pathname;

  return pipe(notification.node, O.fromNullable, O.chain(function (node) {
    if (Challenge.is(node)) return O.some({
      uuid: node.id
    });
    if (PublicResponse.is(node)) return O.some({
      uuid: node.stage.challenge.id,
      activity: getFirstCompletedActivityId(node.stage.challenge.activities),
      scrollTo: node.id
    });
    if (PublicResponseComment.is(node)) return O.some({
      uuid: node.publicResponse.stage.challenge.id,
      activity: getFirstCompletedActivityId(node.publicResponse.stage.challenge.activities),
      scrollTo: node.id
    });
    return O.none;
  }), O.map(function (challengeLinkParams) {
    return createChallengePlayerLink(pathname, challengeLinkParams);
  }), O.match(function () {
    return /*#__PURE__*/_jsx(ClickHandlerComponent, {
      eventType: notification.eventType,
      children: children
    });
  }, function (challengeLink) {
    return /*#__PURE__*/_jsx(OpenChallengePlayer, {
      challengeLink: challengeLink,
      children: children
    });
  }));
};

var TimeStamp = function TimeStamp(_ref7) {
  var createdAt = _ref7.createdAt,
      unread = _ref7.unread,
      currentLang = _ref7.currentLang;
  return /*#__PURE__*/_jsx(ClientSideRender, {
    "data-element": "ClientSideRender",
    "data-component": "TimeStamp",
    "data-source-file": "notification-item.tsx",
    children: /*#__PURE__*/_jsx(Typography, {
      variant: "body2",
      sx: {
        fontWeight: unread ? 'bold' : 'normal',
        color: function color(theme) {
          return unread ? theme.palette.info.main : theme.palette.neutral.main;
        }
      },
      "data-element": "Typography",
      "data-component": "TimeStamp",
      "data-source-file": "notification-item.tsx",
      children: formatDistanceToNow(new Date(createdAt), {
        addSuffix: true,
        locale: getLocale(currentLang)
      })
    })
  });
};

var NotificationItem = function NotificationItem(_ref8) {
  var avatarUrl = _ref8.profile.avatarUrl,
      notification = _ref8.notification,
      hoverEffect = _ref8.hoverEffect;
  var unread = notification.unread,
      iconUrl = notification.iconUrl,
      createdAt = notification.createdAt,
      description = notification.description,
      eventType = notification.eventType;

  var _useLanguage = useLanguage(),
      currentLang = _useLanguage.currentLang;

  return /*#__PURE__*/_jsx(ClickHandler, {
    notification: notification,
    "data-element": "ClickHandler",
    "data-component": "NotificationItem",
    "data-source-file": "notification-item.tsx",
    children: /*#__PURE__*/_jsxs(StyledNotificationItem, {
      hoverEffect: hoverEffect,
      sx: {
        display: 'flex',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2,
        pl: 1,
        pr: 3
      },
      "data-element": "StyledNotificationItem",
      "data-component": "NotificationItem",
      "data-source-file": "notification-item.tsx",
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          display: 'flex',
          alignItems: 'center',
          width: 1
        },
        "data-element": "Box",
        "data-component": "NotificationItem",
        "data-source-file": "notification-item.tsx",
        children: [/*#__PURE__*/_jsx(ItemImage, {
          avatarUrl: avatarUrl,
          iconUrl: iconUrl,
          eventType: eventType,
          "data-element": "ItemImage",
          "data-component": "NotificationItem",
          "data-source-file": "notification-item.tsx"
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            position: 'relative',
            maxWidth: 234
          },
          "data-element": "Box",
          "data-source-file": "notification-item.tsx",
          children: [/*#__PURE__*/_jsx(Typography, {
            component: "span",
            variant: "body2",
            "data-element": "Typography",
            "data-source-file": "notification-item.tsx",
            children: /*#__PURE__*/_jsx(MediaHtml, {
              description: description,
              "data-element": "MediaHtml",
              "data-source-file": "notification-item.tsx"
            })
          }), /*#__PURE__*/_jsx(TimeStamp, {
            createdAt: createdAt,
            unread: unread,
            currentLang: currentLang,
            "data-element": "TimeStamp",
            "data-source-file": "notification-item.tsx"
          })]
        })]
      }), unread && /*#__PURE__*/_jsx(Box, {
        sx: {
          minHeight: 10,
          minWidth: 10,
          backgroundColor: function backgroundColor(theme) {
            return theme.palette.primary.main;
          },
          borderRadius: 50,
          ml: 1
        }
      })]
    })
  });
};

export default NotificationItem;